import { useEffect, useState } from 'react'
import { Users, Gift, Share2, TrendingUp } from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Rows, Empty, CopyButton, Badge } from '../components/UI'
import { get } from '../lib/api'
import { fa, num, faDate, asArray } from '../lib/format'

export default function Referral() {
  const [data, setData] = useState<any>(null)
  useEffect(() => { get('/user/referral').then(setData).catch(() => {}) }, [])
  // اگر سرور «friends» را نفرستد (خطا یا تغییر قالب)، رندر نباید بشکند
  const friends = asArray(data?.friends)

  const stats = [
    { label: 'تعداد دوستان دعوت‌شده', value: fa(data?.count ?? 0), icon: Users, color: '#7C5CFF' },
    { label: 'پورسانت دریافتی (تومان)', value: num(data?.earnings ?? 0), icon: Gift, color: '#00E39A' },
    { label: 'درصد پورسانت شما', value: `${fa(data?.commission ?? 30)}٪`, icon: TrendingUp, color: '#22D3EE' },
  ]

  return (
    <PanelShell title="دعوت دوستان" subtitle="از کارمزد معاملات دوستانتان تا ۳۰٪ پورسانت بگیرید">
      <Card className="relative mb-5 overflow-hidden !p-7">
        <div className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-brand-500/25 blur-[90px]" />
        <div className="pointer-events-none absolute -right-16 bottom-0 h-56 w-56 rounded-full bg-aqua/20 blur-[90px]" />
        <div className="relative">
          <span className="chip mb-4 border-brand-500/30 bg-brand-500/10 text-brand-200">
            <Share2 className="h-3.5 w-3.5" /> برنامه همکاری در فروش
          </span>
          <h2 className="mb-2 text-2xl font-extrabold text-white">هر دوست، یک درآمد دائمی</h2>
          <p className="mb-6 max-w-xl text-sm leading-7 text-slate-400">
            لینک اختصاصی خود را به اشتراک بگذارید. به ازای هر معامله دوستانتان، {fa('۳۰')}٪ از کارمزد
            به‌صورت خودکار و مادام‌العمر به کیف پول شما واریز می‌شود.
          </p>

          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="num overflow-x-auto rounded-xl border border-white/[.07] bg-ink-950/70 px-4 py-3 text-xs text-slate-200" dir="ltr">
              {data?.link ?? '—'}
            </div>
            <CopyButton text={data?.link ?? ''} label="کپی لینک دعوت" />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span>کد معرف شما:</span>
            <span className="num rounded-lg border border-brand-500/30 bg-brand-500/10 px-3 py-1 font-bold tracking-widest text-brand-200" dir="ltr">
              {data?.code ?? '—'}
            </span>
            <CopyButton text={data?.code ?? ''} label="کپی کد" />
          </div>
        </div>
      </Card>

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label}>
            <span className="mb-3 grid h-11 w-11 place-items-center rounded-xl"
              style={{ background: `${s.color}1A`, border: `1px solid ${s.color}44`, color: s.color }}>
              <s.icon className="h-5 w-5" />
            </span>
            <div className="num text-2xl font-extrabold text-white">{s.value}</div>
            <div className="mt-1 text-xs text-slate-500">{s.label}</div>
          </Card>
        ))}
      </div>

      <Card className="!p-0">
        <div className="border-b border-white/[.06] p-4"><h3 className="font-bold text-white">دوستان دعوت‌شده</h3></div>
        <div className="p-2">
          {!data ? <div className="p-3"><Rows n={3} /></div>
            : friends.length === 0 ? <Empty text="هنوز کسی با لینک شما ثبت‌نام نکرده است" icon={<Users className="h-6 w-6" />} />
            : friends.map((f: any, i: number) => (
              <div key={i} className="flex flex-wrap items-center gap-3 border-b border-white/[.04] px-4 py-3 last:border-0">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-500/15 text-xs font-bold text-brand-200">
                  {fa(i + 1)}
                </span>
                <span className="num text-sm text-slate-200" dir="ltr">{f.email}</span>
                <Badge kind={f.level > 0 ? 'up' : 'muted'}>سطح {fa(f.level)}</Badge>
                <span className="mr-auto text-[11px] text-slate-500">{faDate(f.joinedAt, false)}</span>
              </div>
            ))}
        </div>
      </Card>

      <Card className="mt-5">
        <h3 className="mb-4 font-bold text-white">چطور کار می‌کند؟</h3>
        <div className="grid gap-5 sm:grid-cols-3">
          {[
            ['لینک را بفرستید', 'لینک اختصاصی خود را برای دوستانتان ارسال کنید.'],
            ['دوستتان ثبت‌نام می‌کند', 'با ثبت‌نام از طریق لینک شما، به‌صورت خودکار زیرمجموعه شما می‌شود.'],
            ['پورسانت بگیرید', 'از هر معامله او، سهم شما به کیف پولتان واریز می‌شود.'],
          ].map(([t, d], i) => (
            <div key={t}>
              <div className="num mb-2 text-3xl font-black text-white/[.08]">{fa(i + 1)}</div>
              <h4 className="mb-1.5 text-sm font-bold text-white">{t}</h4>
              <p className="text-xs leading-6 text-slate-400">{d}</p>
            </div>
          ))}
        </div>
      </Card>
    </PanelShell>
  )
}
